import { useEffect, useState } from 'react';
import { Box, Button, Stack, Typography, Chip } from '@mui/joy';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { APIFinancePnl, APIFinanceCashflow } from '../../../api';
import {
  useFinanceToken, useIsMobile, PeriodBar, MetricCard, MetricGrid, SectionCard,
  fmtMoney, fmtPct, todayISO, monthStartISO,
} from './financeShared';
import { BreakdownList, PnlDealsList, ManualEntryModal } from './FinanceBreakdown';

export default function FinanceDashboardTab() {
  const token = useFinanceToken();
  const isMobile = useIsMobile();
  const [dateFrom, setDateFrom] = useState(monthStartISO());
  const [dateTo, setDateTo] = useState(todayISO());
  const [pnl, setPnl] = useState(null);
  const [cf, setCf] = useState(null);
  const [loading, setLoading] = useState(false);
  const [entryOpen, setEntryOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setLoading(true);
    Promise.all([
      APIFinancePnl({ date_from: dateFrom, date_to: dateTo }, token),
      APIFinanceCashflow({ date_from: dateFrom, date_to: dateTo }, token),
    ])
      .then(([p, c]) => { if (!cancelled) { setPnl(p); setCf(c); } })
      .catch((e) => console.error('Finance dashboard load', e))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token, dateFrom, dateTo, reloadKey]);

  const chartData = (pnl?.daily?.labels || []).map((label, i) => ({
    label,
    revenue: pnl.daily.revenue?.[i] ?? 0,
    expenses: pnl.daily.expenses?.[i] ?? 0,
    profit: pnl.daily.profit?.[i] ?? 0,
  }));

  const rb = pnl?.revenue_breakdown;
  const eb = pnl?.expense_breakdown;

  return (
    <Box>
      <PeriodBar
        from={dateFrom} to={dateTo}
        onFrom={setDateFrom} onTo={setDateTo}
        onPreset={(f, t) => { setDateFrom(f); setDateTo(t); }}
        loading={loading}
        right={
          <Button size="sm" startDecorator={<AddRoundedIcon />} onClick={() => setEntryOpen(true)} sx={{ borderRadius: 'lg' }}>
            Операция
          </Button>
        }
      />

      <MetricGrid>
        <MetricCard index={0} title="Приход (начислено)" value={fmtMoney(pnl?.revenue)} color="#059669"
          sub={[
            pnl?.revenue_manual ? `вручную ${fmtMoney(pnl.revenue_manual)}` : null,
            pnl?.revenue_excluded ? `не признано ${fmtMoney(pnl.revenue_excluded)}` : null,
          ].filter(Boolean).join(' · ') || undefined} />
        {/* Расход разложен: раньше выезды были растворены в «ЗП», а ЗП
            менеджеров в P&L не попадала вовсе. */}
        <MetricCard index={1} title="Расходы" value={fmtMoney(pnl?.expenses)}
          sub={[
            `исполнители ${fmtMoney(pnl?.payroll)}`,
            pnl?.payroll_transfer ? `выезды ${fmtMoney(pnl.payroll_transfer)}` : null,
            pnl?.payroll_managers ? `менеджеры ${fmtMoney(pnl.payroll_managers)}` : null,
            `операц. ${fmtMoney(pnl?.opex)}`,
          ].filter(Boolean).join(' · ')} color="#f59e0b" />
        <MetricCard index={2} title="Прибыль (PNL)" value={fmtMoney(pnl?.profit)} color="#0ea5e9" />
        <MetricCard index={3} title="Рентабельность" value={fmtPct(pnl?.margin)} color="#6366f1" />
        <MetricCard index={4} title="Сальдо ДДС" value={fmtMoney(cf?.totals?.net)}
          sub={`Приход ${fmtMoney(cf?.totals?.in)} · Расход ${fmtMoney(cf?.totals?.out)}`} color="#8b5cf6" />
      </MetricGrid>

      {/* Ноль в «операционных» — почти всегда не ошибка отчёта, а не
          загруженная выписка банка. Говорим об этом прямо, иначе цифра
          читается как сбой расчёта. */}
      {pnl && !pnl.opex && (
        <Chip color="neutral" variant="soft" sx={{ mb: 2 }}>
          Операционные расходы за период — 0 ₽. Похоже, выписка банка за этот
          период не импортирована: загрузите её (manage.py import_tochka_statement)
          или заведите операции вручную кнопкой «Операция».
        </Chip>
      )}

      {pnl?.manual_touch_count > 0 && (
        <Chip color="warning" variant="soft" sx={{ mb: 2 }}>
          Сделок с ручными передвижениями: {pnl.manual_touch_count} — проверьте в списке ниже
        </Chip>
      )}

      <SectionCard title="Доходы и расходы по дням (по начислению)">
        <Box sx={{ height: { xs: 260, md: 340 }, mx: { xs: -0.5, md: 0 } }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 8, right: 12, left: isMobile ? -18 : 0, bottom: 0 }}>
              <defs>
                <linearGradient id="dashRev" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#059669" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#059669" stopOpacity={0.02} />
                </linearGradient>
                <linearGradient id="dashExp" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#f59e0b" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#f59e0b" stopOpacity={0.02} />
                </linearGradient>
                <linearGradient id="dashPrf" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#6366f1" stopOpacity={0.30} />
                  <stop offset="100%" stopColor="#6366f1" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} minTickGap={16} />
              <YAxis tick={{ fontSize: 11 }} width={isMobile ? 46 : 64}
                tickFormatter={(v) => Number(v).toLocaleString('ru-RU')} />
              <Tooltip formatter={(v) => fmtMoney(v)}
                contentStyle={{ borderRadius: 12, border: '1px solid #e5e7eb', fontSize: 12 }} />
              <Legend />
              <Area type="monotone" dataKey="revenue"  name="Приход"  stroke="#059669" strokeWidth={2} fill="url(#dashRev)" />
              <Area type="monotone" dataKey="expenses" name="Расходы" stroke="#f59e0b" strokeWidth={2} fill="url(#dashExp)" />
              <Area type="monotone" dataKey="profit"   name="Прибыль" stroke="#6366f1" strokeWidth={2} fill="url(#dashPrf)" />
            </AreaChart>
          </ResponsiveContainer>
        </Box>
      </SectionCard>

      {/* ── ПРИХОД: разбивки ── */}
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
        <SectionCard title="Приход по услугам">
          <BreakdownList color="#059669" rows={(rb?.by_service || []).map((r) => ({ label: r.title, amount: r.amount }))} />
          {rb?.additional_services?.amount ? (
            <Typography level="body-xs" sx={{ mt: 1, color: 'text.tertiary' }}>
              Доп. услуги суммарно: {fmtMoney(rb.additional_services.amount)}
            </Typography>
          ) : null}
        </SectionCard>
        <SectionCard title="Приход по доп. услугам">
          <BreakdownList color="#10b981" rows={(rb?.by_additional_service || []).map((r) => ({ label: r.title, amount: r.amount }))} />
        </SectionCard>
      </Box>

      <SectionCard title="Приход по клиентам (компания · ФИО)">
        <BreakdownList color="#0ea5e9" rows={(rb?.by_client || []).map((r) => ({ label: r.name, amount: r.amount }))} />
      </SectionCard>

      {/* ── РАСХОДЫ: разбивки ── */}
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
        <SectionCard title="Расходы по статьям">
          <BreakdownList color="#f59e0b" rows={(eb?.by_category || []).map((r) => ({ label: r.title, amount: r.amount }))} />
        </SectionCard>
        <SectionCard title="Расходы по типам (P&L)">
          <BreakdownList color="#ef4444" rows={(eb?.by_pnl_group || []).map((r) => ({ label: r.title, amount: r.amount }))} />
        </SectionCard>
      </Box>

      <SectionCard title="Расходы по исполнителям">
        <BreakdownList color="#f97316" rows={(eb?.by_employee || []).map((r) => ({ label: r.name, amount: r.amount }))} />
      </SectionCard>

      {/* ЗП менеджеров: оклад + комиссия по плану + KPI-бонус. Раньше эти
          деньги в P&L не участвовали и были видны только в разделе премий. */}
      {(pnl?.payroll_managers_rows?.length > 0) && (
        <SectionCard title="ЗП менеджеров (оклад + комиссия + KPI)">
          <BreakdownList
            color="#a855f7"
            rows={pnl.payroll_managers_rows.map((r) => ({ label: r.name, amount: r.amount }))}
          />
          <Typography level="body-xs" sx={{ mt: 1, color: 'text.tertiary' }}>
            Цели менеджеров помесячные, поэтому месяц, попавший в период
            частично, посчитан пропорционально числу дней.
          </Typography>
        </SectionCard>
      )}

      {/* ── СПИСОК СДЕЛОК ── */}
      <PnlDealsList token={token} dateFrom={dateFrom} dateTo={dateTo} />

      <ManualEntryModal open={entryOpen} onClose={() => setEntryOpen(false)} token={token}
        onSaved={() => setReloadKey((k) => k + 1)} />
    </Box>
  );
}